/**
 * @prettier
 */

import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import { ButtonV2 } from '@zola/zola-ui';
import PhotoUploadIcon from 'assets/images/invitations/single-sample-modal/photo-upload-icon.svg';
import { ptToPx } from '../../../../util';
import generateUUID from '../../../../../lib/uuid';
import { usePhotoObjectURL } from '../util/hooks';
import styles from './styles.module.less';

const UploadButton = ({ disabled, onSelect, children }) => {
  const inputEl = useRef(null);

  const onClick = () => {
    inputEl.current.click();
  };

  const onSelectFile = () => {
    onSelect(inputEl.current.files[0]);
  };

  return (
    <Fragment>
      <input
        ref={inputEl}
        className={styles.hiddenInput}
        type="file"
        accept="image/jpeg,image/png"
        onChange={onSelectFile}
      />
      <ButtonV2 disabled={disabled} onClick={onClick}>
        {children}
      </ButtonV2>
    </Fragment>
  );
};

const Dropzone = ({ busy, form, updateForm, onSuccess, minDimensions }) => {
  const dropzoneIcon = <img alt="" src={PhotoUploadIcon} className={styles.dropzoneIcon} />;

  const { photo } = form.customizations;

  const setPhoto = newPhoto => {
    updateForm({ customizations: { photo: newPhoto } });
  };

  const onSelect = file => {
    if (!file) {
      return;
    }

    const url = URL.createObjectURL(file);
    const imgEl = document.createElement('img');

    imgEl.src = url;

    imgEl.onload = () => {
      const { width, height } = imgEl;

      const size = { width, height };

      const fileMB = file.size / 1024 / 1024;

      const valid = width >= minDimensions.width && height >= minDimensions.height && fileMB < 10;

      const filledSize = Math.max(width, height);

      const canvasEl = document.createElement('canvas');

      canvasEl.width = filledSize;
      canvasEl.height = filledSize;

      const canvasCtx = canvasEl.getContext('2d');

      const drawX = Math.floor((filledSize - width) / 2);
      const drawY = Math.floor((filledSize - height) / 2);

      canvasCtx.fillStyle = 'white';
      canvasCtx.fillRect(0, 0, filledSize, filledSize);
      canvasCtx.drawImage(imgEl, drawX, drawY);

      const saveBlob = displayedBlob => {
        const uuid = generateUUID();

        setPhoto({ id: uuid, file, displayedBlob, valid, size, filledSize });

        if (valid) {
          onSuccess();
        }

        URL.revokeObjectURL(file);
      };

      if (canvasEl.msToBlob) {
        setTimeout(() => saveBlob(canvasEl.msToBlob()), 100);
      } else {
        canvasEl.toBlob(saveBlob);
      }
    };
  };

  return (
    <div className={styles.dropzoneBox}>
      {dropzoneIcon}
      <div className={styles.dropzoneText}>
        Image must be at least {minDimensions.width} by {minDimensions.height} pixels and in JPG or
        PNG format.
        <br />
        (max 10MB)
      </div>
      <div>
        {busy && (
          <UploadButton disabled onSelect={onSelect}>
            Loading...
          </UploadButton>
        )}
        {!busy && (
          <UploadButton onSelect={onSelect}>
            {photo.file && !photo.valid ? 'Try another file' : 'Upload a photo'}
          </UploadButton>
        )}
      </div>
    </div>
  );
};

Dropzone.defaultProps = {
  minDimensions: { width: 1200, height: 1200 },
};

const Crop = ({ form, page, updateForm }) => {
  const { photo } = form.customizations;

  const setPixelCrop = pixelCrop => updateForm({ customizations: { pixelCrop } });

  const photoURL = usePhotoObjectURL(photo);

  const [crop, setCrop] = useState({ aspect: 1, width: 0, x: 0, y: 0 });

  useEffect(() => {
    const customImageElement =
      page && page.elements.find(e => e.content_type === 'IMAGE' && e.customizable);

    if (!customImageElement) {
      return;
    }

    const aspect = customImageElement.width / customImageElement.height;

    setCrop({ aspect, width: ptToPx(customImageElement.width), x: 0, y: 0 });
  }, [page.id]);

  const onChange = (inputCrop, inputPixelCrop) => {
    setCrop(inputCrop);
    setPixelCrop(inputPixelCrop);
  };

  if (!photoURL) {
    return null;
  }

  return (
    <ReactCrop
      className={styles.crop}
      src={photoURL}
      crop={crop}
      onComplete={onChange}
      onChange={onChange}
      keepSelection
    />
  );
};

export default { UploadButton, Dropzone, Crop };



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/PhotoUpload/PhotoUpload.jsx